import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { DeleteResponse, ItemListResponse, SaveResponse } from 'helpers/interfaces';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { ticketContext } from 'views/Ticket/TicketContext';


interface IContext {
    isLoading: boolean
    serviceOrders: ITicketServiceOrder[]
    customerOrders: ITicketCustomerOrder[]
    resolutions: ITicketResolution[]
    defectiveComponents: ITicketDefectiveComponent[]

    saveTicketOrder: (values: Order) => Promise<boolean>
    saveTicketResolution: (values: Resolution) => Promise<{ openDialog: boolean, suggestedStatus?: string }>
    deleteTicketOrder: (type: 'Customer' | 'Service', id: string) => Promise<void>
    deleteTicketResolution: (id: string) => Promise<void>
    deleteDefectiveComponent: (id: string) => Promise<void>
    saveDefectiveComponent: (values: DefectiveComponent) => Promise<boolean>
    defectiveComponentListCompleted: () => Promise<boolean>
}

interface ITicketServiceOrder {
    id: string
    m3OrderNumber: string
    assignmentNumber: string
    createdBy: string
    m3StatusCode: number
    m3StatusDescription: string
    createdOn: moment.Moment
    errorMessage: string
}

interface ITicketCustomerOrder {
    id: string
    m3OrderNumber: string
    createdBy: string
    m3StatusCode: number
    m3StatusDescription: string
    createdOn: moment.Moment
    errorMessage: string
}

export interface ITicketResolution {
    id: string
    internalActions: string
    actionToCustomer: string
    defect: string
    subDefect: string
    notes: string
    nonConformityNumber: string
    errorSymptom: string
    createdBy: string
    createdOn: moment.Moment
}

export interface ITicketDefectiveComponent {
    id: string
    productSKU: string
    productBrand: string
    productDescription: string
    quantity: string
    createdBy: string
    createdOn: moment.Moment
}

export let orderSchema = yup.object({
    type: yup.string().default(''),
    m3orderNumber: yup.string().default('').required('You must provide valid M3 Order Number'),
    assignmentNumber: yup.number()
        .when("type", {
            is: 'Service',
            then: yup.number().min(1, "You must provide valid M3 Assignment Number").default(0),
            otherwise: yup.number().default(0)
        }).default(0)
})

export let resolutionSchema = yup.object({
    id: yup.string().default(''),

    // reference data
    internalActions: yup.string().default(''),
    actionToCustomer: yup.string().default(''),
    defectData: yup.string().default(''),
    defectCause: yup.string().default('').nullable(),
    defect: yup.string().default('').nullable(),
    subDefect: yup.string().default('').nullable(),

    // text field
    notes: yup.string().default(''),
    nonConformityNumber: yup.string().default(''),
    errorSymptom: yup.string().default('')

});

type Order = yup.InferType<typeof orderSchema>;
export type Resolution = yup.InferType<typeof resolutionSchema>;


export let defectiveComponentSchema = yup.object({
    id: yup.string().default(''),
    productSKU: yup.string().default('').required("Please specify defective component"),
    productBrand: yup.string().default(''),
    productDescription: yup.string().default(''),
    quantity: yup.string().default('')
        .test({
            name: "quantity",
            message: "Quantity is mandatory and must be greater than zero",
            test: (value) => Number(value) > 0
        })
});

export type DefectiveComponent = yup.InferType<typeof defectiveComponentSchema>;


export let resolutionsContext = React.createContext({} as IContext);

let { Provider } = resolutionsContext;

let ResolutionsProvider: React.FC<{ children: React.ReactNode, ticketId: string }> = ({ children, ticketId }) => {
    let { enqueueSnackbar } = useSnackbar();

    let [isLoading, setLoading] = useState(true);
    let [serviceOrders, setServiceOrders] = useState<ITicketServiceOrder[]>([]);
    let [customerOrders, setCustomerOrders] = useState<ITicketCustomerOrder[]>([]);
    let [resolutions, setResolutions] = useState<ITicketResolution[]>([]);
    let [defectiveComponents, setDefectiveComponents] = useState<ITicketDefectiveComponent[]>([]);
    let { setStatusDialog, getTicket } = useContext(ticketContext);


    useEffect(() => {
        getTicketOrders(ticketId);
    }, [])


    let getTicketOrders = async (ticketId: string) => {
        try {
            await getServiceOrders(ticketId);
            await getCustomerOrders(ticketId);
            await getManualResolutions(ticketId);
            await getDefectiveComponents(ticketId);
        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let getServiceOrders = async (ticketId: string) => {
        let res = await axios.get<ItemListResponse<ITicketServiceOrder>>('/api/TicketResolution/GetTicketServiceOrders', { params: { ticketId } });
        setServiceOrders(res.data.items);
    }

    let getCustomerOrders = async (ticketId: string) => {
        let res = await axios.get<ItemListResponse<ITicketCustomerOrder>>('/api/TicketResolution/GetTicketCustomerOrders', { params: { ticketId } });
        setCustomerOrders(res.data.items);
    }

    let getManualResolutions = async (ticketId: string) => {
        let res = await axios.get<ItemListResponse<ITicketResolution>>('/api/TicketResolution/GetTicketResolutions', { params: { ticketId } });
        setResolutions(res.data.items);
    }

    let getDefectiveComponents = async (ticketId: string) => {
        let res = await axios.get<ItemListResponse<ITicketDefectiveComponent>>('/api/TicketDefectiveComponent/GetTicketDefectiveComponents', { params: { ticketId } });
        setDefectiveComponents(res.data.items);
    }

    let saveTicketOrder = async (values: Order) => {
        try {
            let request = {
                ...values,
                ticketId
            }

            await axios.post(`/api/TicketResolution/SaveTicket${values.type}Order`, request);
            enqueueSnackbar(`${values.type} order saved`, { variant: 'success' });

            if (values.type === "Customer")
                getCustomerOrders(ticketId);
            else
                getServiceOrders(ticketId);

            return true;

        } catch (err) {
            return false;
        }
    }

    let deleteTicketOrder = async (type: 'Customer' | 'Service', id: string) => {
        if (window.confirm(`Are you sure that you want to remove this ${type.toLowerCase()} order from ticket?`) === false)
            return;

        try {
            await axios.delete<DeleteResponse>(`/api/TicketResolution/DeleteTicket${type}Order/${id}`);
            if (type === "Customer")
                setCustomerOrders(customerOrders.filter(x => x.id !== id));
            else
                setServiceOrders(serviceOrders.filter(x => x.id !== id));

            enqueueSnackbar(`${type} order removed`, { variant: 'success' });
        } catch (err) { }

    }

    let saveTicketResolution = async (values: Resolution) => {
        try {
            let request = {
                ...values,
                ticketId
            }

            let response = await axios.post<SaveResponse>(`/api/TicketResolution/SaveTicketResolution`, request);
            enqueueSnackbar(`Ticket resolution saved`, { variant: 'success' });

            getManualResolutions(ticketId);

            return { openDialog: true, suggestedStatus: response.data.ticketStatusId };

        } catch (err) {
            return { openDialog: false, suggestedStatus: '' };;
        }
    }

    let deleteTicketResolution = async (id: string) => {
        if (window.confirm(`Are you sure that you want to remove this manual resolution from ticket?`) === false)
            return;

        try {
            await axios.delete<DeleteResponse>(`/api/TicketResolution/DeleteTicketResolution/${id}`);
            setResolutions(resolutions.filter(x => x.id !== id));

            enqueueSnackbar(`Ticket resolution removed`, { variant: 'success' });
            setStatusDialog({ open: true })
        } catch (err) { }

    }

    let saveDefectiveComponent = async (values: DefectiveComponent) => {
        try {
            let request = {
                ...values,
                ticketId
            }

            await axios.post(`/api/TicketDefectiveComponent/SaveTicketDefectiveComponent`, request);
            enqueueSnackbar(`Defective component saved`, { variant: 'success' });
            getDefectiveComponents(ticketId);

            return true;

        } catch (err) {
            return false;
        }
    }

    let deleteDefectiveComponent = async (id: string) => {
        if (window.confirm(`Are you sure that you want to remove this defective component from ticket?`) === false)
            return;

        try {
            await axios.delete<DeleteResponse>(`/api/TicketDefectiveComponent/DeleteTicketDefectiveComponent/${id}`);
            setDefectiveComponents(defectiveComponents.filter(x => x.id !== id));

            enqueueSnackbar(`Defective component removed`, { variant: 'success' });
        } catch (err) { }

    }

    let defectiveComponentListCompleted = async () => {
        if (window.confirm(`Are you sure to set as 'complete' this list?\nTicket status will become in progress`) === false)
            return false;

        try {

            let request = {
                ticketId
            }
            await axios.post('/api/TicketDefectiveComponent/CompleteDefectiveComponentsList', request);
            getTicket();
            enqueueSnackbar(`Defective component list set as completed`, { variant: 'success' });
            return true;

        } catch (err) {
            return false;
        }
    }

    return (
        <Provider
            value={{
                isLoading,
                customerOrders,
                serviceOrders,
                resolutions,
                defectiveComponents,

                saveTicketOrder,
                saveTicketResolution,
                deleteTicketOrder,
                deleteTicketResolution,
                saveDefectiveComponent,
                deleteDefectiveComponent,
                defectiveComponentListCompleted
            }}
        >
            {children}
        </Provider>
    )
}

export default ResolutionsProvider;